import axios from 'axios';
import * as Sentry from '@sentry/react';
import type { ConsumableType } from './consumableService';

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export interface StockEntry {
  productId: string;
  productName: string;
  lNumber?: string;
  /**
   * Null when the product's stock ledger is in a unit-conflict state (Slice 4):
   * mixed-family transactions mean the backend can't safely sum a single number.
   * The FE must never do arithmetic on this value without checking for null first;
   * render the `inventory.unitConflict.badge` review pill instead of a number.
   */
  currentStock: number | null;
  consumableType?: ConsumableType;  // e.g. 'chemical' | 'ppe' | 'fertilizer' | 'diesel'
  stockUnit?: string;               // e.g. 'L', 'kg', 'pair'
  deletedAt?: string | null;       // soft-delete timestamp; drives the (deleted) suffix
  /** 'conflict' when currentStock is null due to a cross-family unit conflict. */
  stockStatus?: 'ok' | 'conflict';
  /** Present when stockStatus === 'conflict'; machine reason code (e.g. 'cross_family'). */
  stockConflictReason?: string;
}

export interface InventoryTransaction {
  _id: string;
  tenantId: string;
  productId: string;
  type: 'purchase' | 'consumption' | 'adjustment' | 'reversal';
  quantity: number;
  date: string;
  referenceId?: string;
  attachments?: Array<{
    s3Key: string;
    fileName: string;
    mimeType: string;
    label?: 'invoice' | 'delivery_note' | 'other';
    uploadedAt: string;
  }>;
  createdAt: string;
  // Spec B enrichment (Phase 5): backend resolves the underlying consumable
  // so the UI can render a readable product label without a follow-up fetch.
  consumable?: {
    _id: string;
    productName: string;
    consumableType: ConsumableType;
    lNumber?: string;
    /** Set when the Consumable was soft-deleted. Frontend renders "<name> (deleted)" suffix. */
    deletedAt?: string | null;
  };
  /** Human-readable reference resolved by the backend (e.g. "Session SES-42"). */
  referenceLabel?: string;
  /**
   * Audit marker set by the cascade when the parent SpraySession is soft-deleted.
   * 'orphaned' = the session record was retracted but the physical spray happened;
   * the consumption still counts toward stock totals.
   * Frontend renders a "(from deleted session)" badge on these rows.
   */
  referenceStatus?: 'live' | 'orphaned';
  /** Supplier name on purchase rows (Spec B procurement). */
  supplier?: string;
  /** Unit string for the quantity on purchase rows. */
  quantityUnit?: string;
}

export interface InventoryQuery {
  consumableType?: ConsumableType;
  productId?: string;
  from?: string;
  to?: string;
}

export async function getStockMap(
  params: { consumableType?: ConsumableType; includeEmpty?: boolean } = {}
): Promise<StockEntry[]> {
  const q = new URLSearchParams();
  if (params.consumableType) q.set('consumableType', params.consumableType);
  if (params.includeEmpty) q.set('includeEmpty', 'true');
  const url = `/api/v1/consumables/inventory/stock${q.toString() ? `?${q.toString()}` : ''}`;
  try {
    const res = await axios.get<StockEntry[]>(url, { headers: authHeaders() });
    return res.data;
  } catch (err) {
    Sentry.captureException(err);
    throw err;
  }
}

export async function getStockForProduct(productId: string): Promise<StockEntry> {
  try {
    const res = await axios.get<StockEntry>(
      `/api/v1/consumables/inventory/stock/${productId}`,
      { headers: authHeaders() }
    );
    return res.data;
  } catch (err) {
    Sentry.captureException(err);
    throw err;
  }
}

export async function listTransactions(params: InventoryQuery = {}): Promise<InventoryTransaction[]> {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v != null) q.set(k, String(v)); });
  const url = `/api/v1/consumables/inventory/transactions${q.toString() ? `?${q.toString()}` : ''}`;
  try {
    const res = await axios.get<InventoryTransaction[]>(url, { headers: authHeaders() });
    return res.data;
  } catch (err) {
    Sentry.captureException(err);
    throw err;
  }
}

export interface StockCountBody {
  date: string;          // YYYY-MM-DD
  notes?: string;
  counts: Array<{
    productId: string;
    physicalQuantity: number;
  }>;
}

export interface StockCountResponse {
  referenceId: string;
  rowsRequested: number;
  rowsWritten: number;
  rowsSkippedZeroDelta: number;
  transactions: Array<{
    _id: string;
    productId: string;
    type: 'adjustment';
    quantity: number;
    date: string;
    referenceId: string;
  }>;
}

export async function recordStockCount(body: StockCountBody): Promise<StockCountResponse> {
  try {
    const res = await axios.post<StockCountResponse>(
      '/api/v1/consumables/inventory/count',
      body,
      { headers: authHeaders() },
    );
    return res.data;
  } catch (err) {
    Sentry.captureException(err);
    throw err;
  }
}

export interface UnitDiagnosticRow {
  productId: string;
  productName: string;
  consumableType: 'chemical' | 'fertilizer' | 'diesel';
  stockUnit: string | null;
  doseUnit: string | null;
  reasons: string[];
  onHandStatus: 'ok' | 'conflict' | 'unknown';
}

export async function getUnitDiagnostics(): Promise<UnitDiagnosticRow[]> {
  try {
    const res = await axios.get<UnitDiagnosticRow[]>(
      '/api/v1/consumables/inventory/unit-diagnostics',
      { headers: authHeaders() },
    );
    return res.data;
  } catch (err) {
    Sentry.captureException(err);
    throw err;
  }
}

export interface LedgerRow {
  _id: string;
  date: string | null;
  type: 'purchase' | 'consumption' | 'adjustment' | 'reversal';
  quantity: number;
  quantityUnit: string | null;
  /** null = clean; otherwise why this row is not summable into the stockUnit. */
  reason: 'missing_unit' | 'pack_unit' | 'cross_family' | null;
}

export interface ProductLedgerRows {
  productId: string;
  productName: string;
  consumableType: string;
  stockUnit: string;
  /** Units a relabel may target (the product's stockUnit family). */
  allowedUnits: string[];
  rows: LedgerRow[];
  conflictRows: LedgerRow[];
}

export interface StockLevel {
  onHand: number | null;
  unit: string | null;
  status: 'ok' | 'conflict';
  conflictReason?: string;
}

export async function getProductLedgerRows(productId: string): Promise<ProductLedgerRows> {
  try {
    const res = await axios.get<ProductLedgerRows>(
      `/api/v1/consumables/inventory/unit-diagnostics/${productId}/rows`,
      { headers: authHeaders() },
    );
    return res.data;
  } catch (err) {
    Sentry.captureException(err);
    throw err;
  }
}

/** Relabel one ledger row's unit (quantity untouched). Returns the cleared stock level. */
export async function relabelLedgerRow(
  productId: string,
  rowId: string,
  unit: string,
): Promise<{ before: LedgerRow; after: LedgerRow; level: StockLevel }> {
  try {
    const res = await axios.patch(
      `/api/v1/consumables/inventory/unit-diagnostics/${productId}/rows/${rowId}`,
      { unit },
      { headers: authHeaders() },
    );
    return res.data;
  } catch (err) {
    Sentry.captureException(err);
    throw err;
  }
}
